import _ from 'lodash';
import shallowEqual from 'shallowequal';
import { createSelectorCreator, defaultMemoize } from 'reselect';

import * as statuses from 'app/entity/utils/statuses';
import {
  andify,
  emptyObject,
  ensureArray,
  orify,
} from 'app/common/CommonUtils';

export const createEntitySelector = (selectors, resultFn) => {
  // `reselect` supports variadic selector arguments or an array of selectors,
  // but for consistency, simplicity, and clarity let's enforce an array
  // of selectors to separate them from `resultFn`.
  if (
    !(
      Array.isArray(selectors) &&
      selectors.every(_.isFunction) &&
      _.isFunction(resultFn)
    )
  ) {
    throw new Error(
      'createEntitySelector expects an array of selector functions and a result function as its arguments.'
    );
  }
  const createSelector = createSelectorCreator(defaultMemoize, shallowEqual);
  return createSelector(
    // `reselect` passes two arguments into every selector function:
    // `state` and `props`. `props` is problematic for functional
    // selectors with variadic arguments though and entities should
    // only care about `state`, so force the selectors to only accept
    // a single argument, `state`.
    ...selectors.map(selector => _.ary(selector, 1)),
    resultFn
  );
};

// Takes an `entityName` and returns an object that has a bunch of selector
// methods on it, specifically for that entity. The object is customizable
// via the `customize` argument.
// All methods _directly_ on the returned object apply to the model
// or single entity. All methods on `all` on the returned object apply
// to the collection.
// Having methods for every selector instead of accessing keypaths
// directly allows us to much more easily restructure data in the
// future without having to update a ton of keypaths scattered throughout
// the codebase.
const buildEntitySelectors = (entityName, customize = _.identity) => {
  const keypath = ['entities', entityName];

  const joinMessages = messages => ensureArray(messages).join(', ');

  // ------------------------------------------------------------
  // Model selectors. All of them expect an entity as
  // their final argument.
  // ------------------------------------------------------------
  const model = {};

  // Utility selector indicating whether a given selector returns
  // a value that matches the `value` argument.
  model.matchesSelector = (selector, value, entity) =>
    selector(entity) === value;
  // The unique client-side ID for an entity. Never persisted.
  model.cid = entity => _.get(entity, 'cid');
  model.hasCid = _.partial(model.matchesSelector, model.cid);

  // Local state for entities. Never persisted.
  model.state = entity => _.get(entity, 'state', emptyObject);
  // Retrieves a single state item. Typically partially applied with
  // `kp` (keypath) so the only argument necessary is the `entity`.
  model.stateItem = (kp, entity) => _.get(model.state(entity), kp);
  // Indicates whether the entity has been persisted to the server.
  // Useful to differentiate client-side only entities.
  model.isPersisted = entity =>
    model.stateItem('isPersisted', entity) !== false;

  // Statuses, stored in entity state.
  model.status = _.partial(model.stateItem, 'status');
  model.hasStatus = _.partial(model.matchesSelector, model.status);
  model.statusCode = _.partial(model.stateItem, 'statusCode');
  model.hasStatusCode = _.partial(model.matchesSelector, model.statusCode);
  model.isLoading = _.partial(model.hasStatus, statuses.LOADING);
  model.isInitial = _.partial(model.hasStatus, statuses.INITIAL);
  model.isFailure = _.partial(model.hasStatus, statuses.FAILURE);
  model.isNotFound = _.partial(model.hasStatusCode, statuses.NOT_FOUND);
  model.isSaving = _.partial(model.hasStatus, statuses.SAVING);
  model.isSuccess = _.partial(model.hasStatus, statuses.SUCCESS);
  model.isTesting = _.partial(model.hasStatus, statuses.TESTING);
  // Whether the entity is currently loading or hasn't loaded at all yet.
  model.isLoadImpending = entity =>
    model.isLoading(entity) || model.isInitial(entity);
  // Raw status messages. Sometimes success messages, but typically used
  // to access `errors` from the server.
  model.statusMessages = _.partial(model.stateItem, 'statusMessages');
  // Combines all status messages into a single string.
  model.statusMessage = _.flow(
    model.statusMessages,
    joinMessages
  );
  model.failureMessages = entity =>
    model.isFailure(entity) ? model.statusMessages(entity) : [];

  // Persisted data for entities.
  model.data = entity => _.get(entity, 'data', emptyObject);
  // Like `model.stateItem`, `kp` is typically partially applied so only
  // `entity` needs to be supplied as an argument.
  model.dataItem = (kp, entity) => _.get(model.data(entity), kp);
  // Common data selectors for all entities.
  model.id = _.partial(model.dataItem, 'id');
  model.hasId = _.partial(model.matchesSelector, model.id);
  model.dateCreated = _.partial(model.dataItem, 'created_at');
  model.accountId = _.partial(model.dataItem, 'account_id');
  model.isInAccount = _.partial(model.matchesSelector, model.accountId);

  // ------------------------------------------------------------
  // Collection selectors. All of them expect the full store
  // state as their final argument.
  // ------------------------------------------------------------
  const entitiesObj = state =>
    _.get(state, [...keypath, 'entities'], emptyObject);

  const all = {};

  // Local state for a collection. Never persisted.
  all.state = state => _.get(state, [...keypath, 'state'], emptyObject);
  all.stateItem = (kp, state) => _.get(all.state(state), kp);

  // Persisted data for a collection.
  all.data = state => _.get(state, [...keypath, 'data'], emptyObject);
  all.dataItem = (kp, state) => _.get(all.data(state), kp);

  // Array of entities within a collection.
  all.entities = createEntitySelector([entitiesObj], entities =>
    _.values(entities).filter(Boolean)
  );

  // Array of entities' raw persisted data within a collection.
  all.entitiesData = createEntitySelector([all.entities], entities =>
    entities.map(model.data)
  );
  // Object of entities' raw persisted data within a collection.
  // Typically used for legacy code that existed before entities.
  all.entitiesDataById = createEntitySelector([entitiesObj], entities =>
    _.mapValues(entities, model.data)
  );
  // Array of all entity `id`s.
  all.entitiesIds = createEntitySelector([all.entities], entities =>
    entities.map(model.id)
  );
  // Whether the collection is empty.
  all.isEmpty = state => all.entities(state).length === 0;
  // First entity within a collection.
  all.first = _.flow(
    all.entities,
    _.first
  );
  // Clearer intent for `all.first`.
  all.only = all.first;

  // Array of all entities that match `predicate`, where `predicate`
  // often is a version of `model.matchesSelector`.
  all.filterBy = (predicate, ...args) => {
    const predArgs = _.initial(args);
    const state = _.last(args);
    return all.entities(state).filter(entity => predicate(...predArgs, entity));
  };
  // First entity that matches `predicate`, where `predicate` often
  // is a version of `model.matchesSelector`.
  all.findBy = (predicate, ...args) => {
    const predArgs = _.initial(args);
    const state = _.last(args);
    return _.find(all.entities(state), entity =>
      predicate(...predArgs, entity)
    );
  };
  // Returns a boolean indicating whether any entity within the collection
  // matches `predicate`, where `predicate` often is a version of `model.matchesSelector`.
  all.hasAnyBy = (predicate, ...args) => {
    const predArgs = _.initial(args);
    const state = _.last(args);
    return all.entities(state).some(entity => predicate(...predArgs, entity));
  };
  // Common collection filters.
  all.failedEntities = _.partial(all.filterBy, model.isFailure);
  all.successfulEntities = _.partial(all.filterBy, model.isSuccess);
  all.persistedEntities = _.partial(all.filterBy, model.isPersisted);
  all.persistedEntitiesData = _.flow(
    _.partial(all.persistedEntities),
    _.partial(_.map, _, model.data)
  );
  all.inAccount = _.partial(all.filterBy, model.isInAccount);

  // Selects a single entity by its `id`.
  all.entity = (id, state) => _.get(entitiesObj(state), id);

  // Selects a single entity by its `cid`.
  all.entityByCid = _.partial(all.findBy, model.hasCid);

  // Collection statuses, stored in collection state.
  all.status = _.partial(all.stateItem, 'status');
  all.hasStatus = (status, state) => all.status(state) === status;
  all.isStatusUndefined = _.partial(all.hasStatus, undefined);
  all.isLoading = _.partial(all.hasStatus, statuses.LOADING);
  all.isSaving = _.partial(all.hasStatus, statuses.SAVING);
  all.isSavingAny = _.partial(all.hasAnyBy, model.isSaving);
  all.isInitial = _.partial(all.hasStatus, statuses.INITIAL);
  all.isPartiallyHydrated = _.partial(
    all.hasStatus,
    statuses.PARTIALLY_HYDRATED
  );
  all.isFailure = _.partial(all.hasStatus, statuses.FAILURE);
  all.isSuccess = _.partial(all.hasStatus, statuses.SUCCESS);
  all.isUnloaded = orify(all.isInitial, all.isStatusUndefined);
  // Indicates whether the collection is loading or whether it
  // hasn't loaded at all yet. This is the most useful loading selector.
  all.isLoadImpending = orify(all.isLoading, all.isUnloaded);
  all.isInitialLoad = andify(all.isLoadImpending, all.isEmpty);
  all.hasLoaded = _.negate(all.isLoadImpending);
  all.hasFailedEntities = _.partial(all.hasAnyBy, model.isFailure);
  // Indicates whether a collection should load, defaulting to `true`.
  // Override in a specific entity to prevent overfetching, like if
  // data would never need to be refetched during a session.
  all.shouldLoad = () => true;
  // Raw status messages. Sometimes success messages, but typically used
  // to access `errors` from the server.
  all.statusMessages = _.partial(all.stateItem, 'statusMessages');
  // Combines all status messages into a single string.
  all.statusMessage = _.flow(
    all.statusMessages,
    joinMessages
  );

  // Entity statuses, accessed via the collection by an entity's `id`.
  // This prevents needing to select the entity and then select its
  // status. Pure sugar.
  all.isLoadingById = _.flow(
    all.entity,
    model.isLoading
  );
  all.isInitialById = _.flow(
    all.entity,
    model.isInitial
  );
  all.isFailureById = _.flow(
    all.entity,
    model.isFailure
  );
  all.isSavingById = _.flow(
    all.entity,
    model.isSaving
  );
  all.isSuccessById = _.flow(
    all.entity,
    model.isSuccess
  );
  all.isTestingById = _.flow(
    all.entity,
    model.isTesting
  );
  all.isLoadImpendingById = _.flow(
    all.entity,
    model.isLoadImpending
  );

  // Entity statuses, accessed via the collection by an entity's `cid`.
  // This prevents needing to select the entity and then select its
  // status. Pure sugar.
  all.isLoadingByCid = _.flow(
    all.entityByCid,
    model.isLoading
  );
  all.isInitialByCid = _.flow(
    all.entityByCid,
    model.isInitial
  );
  all.isFailureByCid = _.flow(
    all.entityByCid,
    model.isFailure
  );
  all.isSavingByCid = _.flow(
    all.entityByCid,
    model.isSaving
  );
  all.isSuccessByCid = _.flow(
    all.entityByCid,
    model.isSuccess
  );
  all.isTestingByCid = _.flow(
    all.entityByCid,
    model.isTesting
  );
  all.isLoadImpendingByCid = _.flow(
    all.entityByCid,
    model.isLoadImpending
  );

  // Bundle up all the selectors and return them.
  const selectors = { all, ...model };
  customize(selectors);
  return selectors;
};

export default buildEntitySelectors;
